import { Fragment, useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../msalConfig';
import UndoToast from '../watch/UndoToast';
import { createPending, UNDO_MS } from '../watch/pendingActions';
import CollapsibleSection, { openSection } from '../common/CollapsibleSection';
import { loadHomes, loadHomePhotos, patchHome, uploadHomePhoto } from './homesApi';
import {
  FACTORS, FACTOR_KEYS, SORTS, MPH, MILE_COST, rankHomes, boardView, SWING_PROGRAMS, SWING_LABELS, summary, headerSummary, watchSet, inactiveWatch,
  isArchived, isWatch, normalizeMode, modeLabel, verifyItems, PROGRAMS, normalizeProgram, commuteLines,
  openHouseState, openHouseText, openUntilText,
  money, shortDate, trendLine, timeline, dropBadgeText, newDropText, statusTone, statusBadge, unscoredText, fitColor,
} from './homesCalc';
import HomesScatter from './HomesScatter';
import PhotoDropZone from './PhotoDropZone';
import './Homes.css';

// ============================================================
// HOMES — the "best house" board inside the Shopping tab.
//
// Self-contained: loads Homes_Watch itself, renders NOTHING when there are no
// non-archived Watch rows, and tells the parent the count (onCount) so the
// Shopping page can show its "Homes (N) ↓" jump link. Wrapped in a
// CollapsibleSection (id "homes"); the collapsed header still shows the price
// drop count in red.
//
// Ranking: SCORE chips pick Fit (all eight factors) or a Custom mix; SORT
// chips optionally reorder by one metric (the score stays visible, dimmed).
// Both are remembered in localStorage "homes.rank.v1".
//
// SWIM PROGRAM chips pick whose commute counts (TWST / Montgomery / Conroe /
// Magnolia / Hedge): the commute bar, the Fit commute term, true cost and the
// More panel follow it. Remembered in localStorage "homes.program"; first
// visit or anything unreadable → TWST.
//
// VS #1: the first card in the current view is the benchmark; every other card
// shows its commute, rent and true cost difference from it (green better, red
// worse, gray equal). On plain Fit a pill says how far behind #1 it is (tied /
// close / behind, bands in homesCalc.js) and a divider closes the tied group.
// The swing strip shows the house's place under each swim program.
//
// VIEW: Rows (default) = one compact ~60px row per house; tapping a row opens
// its full card in place beneath it (one at a time, remembered in the URL
// hash #HM-00005). Cards = every full card, as before. The pick is kept in
// localStorage "homes.view". The program / score / sort chips stick under the
// app nav while the list scrolls, and a reorder slides rows to their new places
// (FLIP, skipped for prefers-reduced-motion).
//
// OPEN HOUSES: a green dot (photo / thumbnail, top-left) while any recorded
// window is still ahead; "Open house Sun 1–3 PM" on the card; "OPEN NOW" inside
// a window. The board's clock ticks every minute so these come and go on time.
// The "Open soon" sort shows only houses open now or opening within 48 h.
//
// Ranking, drops and every display string come from homesCalc.js. Writes are
// only hand-edited fields (WatchStatus, Notes, PhotoRef); prices change only
// when Claude in chat sends new HAR facts to the server.
// ============================================================

export const RANK_KEY = 'homes.rank.v1';
export const PROGRAM_KEY = 'homes.program';
export const VIEW_KEY = 'homes.view';
const VIEWS = ['rows', 'cards'];
const HASH_RE = /^#(HM-\d{5})$/;
const ACC_MS = 220;
const FLIP_MS = 250;
const LEASE_OPTION_TONE = { Open: 'good', Asked: 'warn', No: 'bad' };
const SORT_KEYS = SORTS.map((s) => s.key);

/** { mode, sort } from localStorage; anything unreadable → Fit, no sort. */
export function readRank() {
  try {
    const v = JSON.parse(window.localStorage.getItem(RANK_KEY) || 'null');
    return { mode: normalizeMode(v?.mode), sort: SORT_KEYS.includes(v?.sort) ? v.sort : null };
  } catch {
    return { mode: normalizeMode(null), sort: null };
  }
}
function writeRank(rank) {
  try { window.localStorage.setItem(RANK_KEY, JSON.stringify(rank)); } catch { /* storage blocked: session only */ }
}
/** Saved swim program; missing, unknown or unreadable → TWST. */
export function readProgram() {
  try { return normalizeProgram(window.localStorage.getItem(PROGRAM_KEY)); } catch { return normalizeProgram(null); }
}
function writeProgram(program) {
  try { window.localStorage.setItem(PROGRAM_KEY, program); } catch { /* storage blocked: session only */ }
}
/** Saved view; missing, unknown or unreadable → rows. */
export function readView() {
  try { const v = window.localStorage.getItem(VIEW_KEY); return VIEWS.includes(v) ? v : 'rows'; } catch { return 'rows'; }
}
function writeView(view) {
  try { window.localStorage.setItem(VIEW_KEY, view); } catch { /* storage blocked: session only */ }
}
/** The open house from the URL hash (#HM-00005), or null. */
export function readHashId() {
  const m = HASH_RE.exec(window.location.hash || '');
  return m ? m[1] : null;
}
function writeHashId(homeId) {
  const { pathname, search } = window.location;
  try { window.history.replaceState(window.history.state, '', homeId ? `#${homeId}` : `${pathname}${search}`); } catch { /* no history API: hash not kept */ }
}
const TICK_MS = 60_000;

/** The open-house line for a card, or null. */
function OpenHouseLine({ oh, now }) {
  if (!oh.next) return null;
  return (
    <div className="Homes-oh" data-testid="open-house">
      {oh.now
        ? <><span className="Homes-oh-now">OPEN NOW</span> {openUntilText(oh.now)}</>
        : <>Open house {openHouseText(oh.next, now)}</>}
    </div>
  );
}

const reducedMotion = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function baths(r) {
  const f = typeof r.BathsFull === 'number' ? r.BathsFull : null;
  const h = typeof r.BathsHalf === 'number' ? r.BathsHalf : null;
  if (f === null && h === null) return null;
  return `${(f || 0) + (h || 0) * 0.5} ba`;
}

function factsLine(r, newnessLabel) {
  return [
    typeof r.Beds === 'number' ? `${r.Beds} bd` : null,
    baths(r),
    typeof r.SqFt === 'number' && r.SqFt > 0 ? `${r.SqFt.toLocaleString('en-US')} sq ft` : null,
    newnessLabel,
  ].filter(Boolean).join(' · ');
}

function yearLabel(r) {
  const b = typeof r.YearBuilt === 'number' && r.YearBuilt > 0 ? r.YearBuilt : null;
  const v = typeof r.RenovatedYear === 'number' && r.RenovatedYear > 0 ? r.RenovatedYear : null;
  if (b && v && v > b) return `${b} (reno ${v})`;
  return b ? String(b) : v ? `reno ${v}` : null;
}

function FactorBars({ sub, mode }) {
  const custom = mode.kind === 'custom';
  return (
    <div className="Homes-bars">
      {FACTORS.map((f) => {
        const v = sub?.[f.key];
        const dim = custom && !mode.factors.includes(f.key);
        return (
          <div key={f.key} className={`Homes-bar${dim ? ' Homes-bar--dim' : ''}`} data-testid={`bar-${f.key}`} data-value={v ?? ''}>
            <span className="Homes-bar-label">{f.label}</span>
            {v === null || v === undefined ? (
              <span className="Homes-bar-track Homes-bar-track--none"><span className="Homes-bar-none">not scored</span></span>
            ) : (
              <span className="Homes-bar-track" title={`${f.label} ${Math.round(v)}`}>
                <span className="Homes-bar-fill" style={{ width: `${Math.max(2, v)}%`, background: fitColor(v) }} />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

const TIER_PILL = { tied: 'Homes-tier--tied', close: 'Homes-tier--close', behind: 'Homes-tier--behind' };

function VsLine({ vm }) {
  if (vm.benchmark) return <div className="Homes-vs" data-testid="vs-line"><span className="Homes-bench">Benchmark</span></div>;
  if (!vm.vs?.length) return null;
  return (
    <div className="Homes-vs" data-testid="vs-line">
      <span className="Homes-vs-label">vs #1</span>
      {vm.vs.map((t) => (
        <span key={t.key} className={`Homes-vs-term Homes-vs-term--${t.tone}`} data-term={t.key} data-tone={t.tone}>
          {t.text}{t.milesText && <span className="Homes-vs-miles" data-testid="vs-miles"> · {t.milesText}</span>}
        </span>
      ))}
    </div>
  );
}

/** Compact vs line for a row: "+1.2 h · 35 mi  −$300  true +$85" (title = the full form). */
function VsCompact({ vm }) {
  if (vm.benchmark) return <span className="Homes-bench">Benchmark</span>;
  if (!vm.vs?.length) return null;
  return (
    <span className="Homes-row-vs" data-testid="row-vs" title={`vs #1: ${vm.vs.map((t) => t.text + (t.milesText ? ` · ${t.milesText}` : '')).join(' · ')}`}>
      {vm.vs.map((t) => (
        <span key={t.key} className={`Homes-vs-term Homes-vs-term--${t.tone}`} data-term={t.key}>
          {t.short}{t.milesText && <span className="Homes-vs-miles"> · {t.milesText}</span>}
        </span>
      ))}
    </span>
  );
}

/** One compact row: a button that opens the full card beneath it. */
function HomeRow({ vm, program, photo, open, onToggle, panelId, now }) {
  const r = vm.row;
  const oh = openHouseState(r, now);
  const gone = statusBadge(r.LeaseStatus);
  const cut = typeof r.PriceCutCount === 'number' && r.PriceCutCount > 0;
  const dropTitle = cut ? (vm.drop.dropped ? `Dropped ${money(vm.drop.amount)} (−${vm.drop.pct}%) · cut ${r.PriceCutCount}×` : `Price cut ${r.PriceCutCount}×`) : null;
  return (
    <button type="button" className={`Homes-row${vm.tier?.kind === 'behind' ? ' Homes-row--behind' : ''}${open ? ' Homes-row--open' : ''}`}
      aria-expanded={open} aria-controls={panelId} onClick={onToggle} data-testid="home-row" data-home={r.HomeId}>
      <span className="Homes-row-score">
        <span className="Homes-row-rank">#{vm.rank}</span>
        <span className="Homes-row-fit" style={{ color: fitColor(vm.score.score) }} data-testid="row-fit">{vm.score.score ?? '—'}</span>
        {vm.tier && !vm.benchmark && (
          <span className={`Homes-tier ${TIER_PILL[vm.tier.kind]}`} data-testid="tier" data-tier={vm.tier.kind}>{vm.tier.kind === 'tied' ? '≈ #1' : `−${vm.tier.behind}`}</span>
        )}
      </span>
      <span className="Homes-row-thumb">
        {photo?.thumb ? <img src={photo.thumb} alt="" loading="lazy" /> : <span className="Homes-row-nophoto" aria-hidden="true">⌂</span>}
        {gone && <span className={`Homes-row-status Homes-row-status--${gone.kind}`} title={gone.label} aria-label={gone.label} data-testid="row-status" />}
        {oh.active && <span className="Homes-oh-dot Homes-oh-dot--row" title={oh.now ? 'Open now' : `Open house ${openHouseText(oh.next, now)}`} aria-label="Open house" data-testid="oh-dot" />}
      </span>
      <span className="Homes-row-main">
        <span className="Homes-row-line">
          <span className="Homes-row-addr"><strong>{r.Title || r.HomeId}</strong>{r.Subdivision && <span className="Homes-subdiv"> · {r.Subdivision}</span>}</span>
          {oh.now && <span className="Homes-oh-now Homes-oh-now--row" data-testid="row-open-now">OPEN NOW</span>}
          {typeof r.LeasePrice === 'number' && <span className="Homes-row-rent">{money(r.LeasePrice)}</span>}
          {cut && <span className="Homes-row-cut" title={dropTitle} aria-label={dropTitle} data-testid="row-cut">↓</span>}
        </span>
        <span className="Homes-row-line">
          <VsCompact vm={vm} />
          <span className="Homes-row-dots" aria-label="Rank by club">
            {SWING_PROGRAMS.map((p) => (
              <span key={p.key} className="Homes-row-club" title={`Rank if ${p.name}`} aria-label={`Rank if ${p.name}: ${vm.swing.ranks[p.key] ?? 'not scored'}`}>
                <span className={`Homes-row-dot${program === p.key ? ' Homes-row-dot--on' : ''}`} data-testid={`row-dot-${p.key}`}>{vm.swing.ranks[p.key] ?? '–'}</span>
                <span className="Homes-row-club-name" aria-hidden="true"><span className="Homes-club-tiny">{p.tiny}</span><span className="Homes-club-letter">{p.letter}</span></span>
              </span>
            ))}
          </span>
        </span>
      </span>
    </button>
  );
}

/** Height-animated panel; children stay mounted until the close animation ends. */
function Accordion({ id, open, children }) {
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) { setMounted(true); return undefined; }
    const t = setTimeout(() => setMounted(false), ACC_MS);
    return () => clearTimeout(t);
  }, [open]);
  return (
    <div id={id} className={`Homes-acc${open ? ' Homes-acc--open' : ''}`} aria-hidden={!open} data-testid="row-panel">
      <div className="Homes-acc-inner">{(open || mounted) && children}</div>
    </div>
  );
}

function SwingStrip({ swing, program }) {
  return (
    <div className="Homes-swing" data-testid="swing">
      <div className="Homes-swing-dots">
        {SWING_PROGRAMS.map((p) => {
          const rank = swing.ranks[p.key];
          return (
            <span key={p.key} className={`Homes-swing-dot${program === p.key ? ' Homes-swing-dot--on' : ''}`} data-program={p.key} data-testid={`swing-${p.key}`} title={`${p.short} rank ${rank ?? 'not scored'}`}>
              <span className="Homes-swing-num">{rank ?? '–'}</span>
              <span className="Homes-swing-name">{p.short}</span>
            </span>
          );
        })}
      </div>
      {swing.label && <span className={`Homes-swing-label Homes-swing-label--${swing.label}`} data-testid="swing-label">{SWING_LABELS[swing.label]}</span>}
    </div>
  );
}

function HomeCard({ vm, mode, program, sorted, photo, expanded, onToggle, onDrop, onSaveNotes, onPhoto, uploading, zoneOpen, onZone, today }) {
  const { row: r, score, drop, calc, sub, cost, commute } = vm;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(r.Notes || '');
  const [verifyOpen, setVerifyOpen] = useState(false);
  const badge = dropBadgeText(drop);
  const newDrop = newDropText(drop, { today });
  const trend = trendLine(r, { today });
  const tone = statusTone(r.LeaseStatus);
  const gone = statusBadge(r.LeaseStatus);
  const checks = verifyItems(r.VerifyNote);
  const oh = openHouseState(r, today);
  const hist = expanded ? timeline(r) : [];

  return (
    <article className={`Homes-card${drop.dropped ? ' Homes-card--drop' : ''}${vm.tier?.kind === 'behind' ? ' Homes-card--behind' : ''}`} data-testid="home-card" data-home={r.HomeId}>
      <header className={`Homes-card-head${sorted ? ' Homes-card-head--dim' : ''}`}>
        <span className="Homes-rank">#{vm.rank}</span>
        <span className="Homes-fit" style={{ color: fitColor(score.score) }} data-testid="score">{score.score ?? '—'}</span>
        <span className="Homes-fit-label">{modeLabel(mode)}</span>
        {vm.tier && !vm.benchmark && (
          <span className={`Homes-tier ${TIER_PILL[vm.tier.kind]}`} data-testid="tier" data-tier={vm.tier.kind}>
            {vm.tier.kind === 'tied' ? '≈ #1' : `−${vm.tier.behind}`}
          </span>
        )}
        {score.score === null
          ? <span className="Homes-partial">not scored</span>
          : score.partial && <span className="Homes-partial" data-testid="unscored">· {unscoredText(score.skipped)}</span>}
      </header>

      <div className="Homes-card-top">
        <div className="Homes-photo">
          {photo?.thumb
            ? <img src={photo.thumb} alt={r.Title || 'House'} loading="lazy" />
            : <div className="Homes-photo-none">No photo</div>}
          {badge && (
            <div className="Homes-drop-badge" data-testid="drop-badge">
              <span>{badge}</span>
              {drop.cuts > 0 && <span className="Homes-drop-cuts">cut {drop.cuts}×</span>}
            </div>
          )}
          {oh.active && <div className="Homes-oh-dot" title={oh.now ? 'Open now' : `Open house ${openHouseText(oh.next, today)}`} aria-label="Open house" data-testid="oh-dot" />}
          {gone && <div className={`Homes-status-badge Homes-status-badge--${gone.kind}${oh.active ? ' Homes-status-badge--shift' : ''}`} data-testid="status-badge">{gone.label}</div>}
          {drop.rise && <div className="Homes-rise-badge">↑ {money(drop.rise.amount)}</div>}
        </div>
        <FactorBars sub={sub} mode={mode} />
      </div>

      {newDrop && (
        <div className="Homes-newdrop" data-testid="new-drop"><strong>NEW DROP</strong> · {newDrop}</div>
      )}

      <div className="Homes-addr">
        <span className="Homes-street">{r.Title || r.HomeId}</span>
        {r.Subdivision && <span className="Homes-subdiv"> · {r.Subdivision}</span>}
      </div>
      <div className="Homes-price">
        {typeof r.LeasePrice === 'number' && <span className="Homes-rent">{money(r.LeasePrice)}<small>/mo</small></span>}
        {calc.pricePerSqFt !== null && <span className="Homes-ppsf">${calc.pricePerSqFt.toFixed(2)}/sq ft</span>}
      </div>
      {cost && <div className="Homes-truecost" data-testid="true-cost">True cost ≈ {money(cost.total)}/mo (rent + driving)</div>}
      <VsLine vm={vm} />
      <SwingStrip swing={vm.swing} program={program} />
      {commute.fallback && <div className="Homes-commute-hint" data-testid="program-hint">Commute not scored for this program · showing TWST</div>}
      {factsLine(r, yearLabel(r)) && <div className="Homes-facts">{factsLine(r, yearLabel(r))}</div>}
      {trend && <div className={`Homes-trend${drop.dropped ? ' Homes-trend--drop' : ''}`}>{trend}</div>}
      <OpenHouseLine oh={oh} now={today} />

      <div className="Homes-pills">
        {r.LeaseStatus && <span className={`Homes-pill Homes-pill--${tone}`}>{r.LeaseStatus}</span>}
        {r.ForSale === true && <span className="Homes-pill Homes-pill--sale">For sale{typeof r.SalePrice === 'number' ? ` ${money(r.SalePrice)}` : ''}</span>}
        {r.LeaseOption && r.LeaseOption !== 'Unknown' && <span className={`Homes-pill Homes-pill--${LEASE_OPTION_TONE[r.LeaseOption] || 'plain'}`}>Lease option: {r.LeaseOption}</span>}
        {r.Garage && <span className="Homes-pill Homes-pill--plain">Garage {r.Garage}</span>}
        {checks.length > 0 && (
          <button type="button" className="Homes-pill Homes-pill--verify" aria-expanded={verifyOpen} onClick={() => setVerifyOpen((v) => !v)} data-testid="verify-pill">
            ⚠ Verify {checks.length}
          </button>
        )}
      </div>
      {verifyOpen && checks.length > 0 && (
        <ul className="Homes-verify" data-testid="verify-list">
          {checks.map((c, i) => <li key={i}><span aria-hidden="true">☐</span> {c}</li>)}
        </ul>
      )}

      {r.Notes && !editing && <p className="Homes-notes">{r.Notes}</p>}
      {editing && (
        <div className="Homes-notes-edit">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} aria-label="Notes" />
          <div>
            <button type="button" className="Homes-btn Homes-btn--primary" onClick={() => { onSaveNotes(r, draft); setEditing(false); }}>Save</button>
            <button type="button" className="Homes-btn" onClick={() => { setDraft(r.Notes || ''); setEditing(false); }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="Homes-actions">
        {r.HarUrl && <a className="Homes-btn" href={r.HarUrl} target="_blank" rel="noopener noreferrer">HAR ↗</a>}
        <button type="button" className="Homes-btn" onClick={() => { setDraft(r.Notes || ''); setEditing((v) => !v); }}>Notes</button>
        <button type="button" className="Homes-btn" disabled={uploading} aria-expanded={zoneOpen} onClick={() => onZone(!zoneOpen)}>{uploading ? 'Uploading…' : (photo ? 'New photo' : '+ Photo')}</button>
        <button type="button" className="Homes-btn Homes-btn--more" onClick={onToggle} aria-expanded={expanded}>{expanded ? 'Less ▴' : 'More ▾'}</button>
        <button type="button" className="Homes-btn Homes-btn--drop" onClick={() => onDrop(r)} aria-label={`Drop ${r.Title || r.HomeId}`}>Drop ✕</button>
      </div>

      {zoneOpen && (
        <PhotoDropZone busy={uploading} onClose={() => onZone(false)}
          onFile={async (f) => { if (await onPhoto(r, f)) onZone(false); }} />
      )}

      {expanded && (
        <div className="Homes-more">
          {[['Commute', commute.hours != null ? `${commute.hours} h/week` : null, commuteLines(r, program).join('\n') || null],
            ['Trails', r.TrailScore != null ? `${r.TrailScore}/5` : null, r.TrailNote],
            ['Family', r.FamilyScore != null ? `${r.FamilyScore}/5` : null, r.FamilyNote],
            ['Patio', r.PatioScore != null ? `${r.PatioScore}/5` : null, r.PatioNote]].map(([label, val, note]) => (
            <div key={label} className="Homes-more-row">
              <span className="Homes-more-label">{label}</span>
              <span>
                {val || <em>not scored</em>}
                {note && <span className="Homes-more-note" data-testid={`note-${label.toLowerCase()}`}>{note}</span>}
              </span>
            </div>
          ))}
          {cost && (
            <p className="Homes-assume">
              True cost assumes driving at ${MILE_COST.toFixed(2)}/mile and {MPH} mph average
              ({commute.hours} h/week ≈ {cost.milesPerWeek} miles/week ≈ {money(cost.driving)}/mo).
            </p>
          )}
          <div className="Homes-timeline">
            <div className="Homes-more-label">Price timeline</div>
            {hist.length === 0 ? <em>No price history yet.</em> : (
              <table>
                <tbody>
                  {hist.map((e, i) => (
                    <tr key={`${e.date}-${i}`}>
                      <td>{shortDate(e.date, { today })}</td>
                      <td>{typeof e.price === 'number' ? money(e.price) : ''}</td>
                      <td>{e.status || ''}</td>
                      <td className="Homes-timeline-src">{e.source || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

export default function HomesSection({ onCount }) {
  const { instance, accounts } = useMsal();
  const [rows, setRows] = useState([]);
  const [photos, setPhotos] = useState(new Map());
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [rank, setRankState] = useState(readRank);
  const [program, setProgramState] = useState(readProgram);
  const [view, setViewState] = useState(readView);
  const [openId, setOpenId] = useState(readHashId);
  const [dropsOnly, setDropsOnly] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [uploading, setUploading] = useState(null);
  const [photoZone, setPhotoZone] = useState(null);   // row id whose drop zone is open (one at a time)
  const [toast, setToast] = useState(null);
  const pendingRef = useRef(null);
  if (!pendingRef.current) pendingRef.current = createPending({ delayMs: UNDO_MS });

  const setRank = useCallback((fn) => setRankState((cur) => { const next = fn(cur); writeRank(next); return next; }), []);
  const mode = rank.mode;
  // FLIP: a reorder snapshots every item's page position first; the layout effect below slides them from there.
  const itemRefs = useRef(new Map());
  const flipFrom = useRef(null);
  const stickyRef = useRef(null);
  const captureFlip = useCallback(() => {
    if (reducedMotion()) return;
    const tops = new Map();
    itemRefs.current.forEach((el, id) => { if (el) tops.set(id, el.getBoundingClientRect().top + window.scrollY); });
    flipFrom.current = tops;
  }, []);
  const setRankFlip = useCallback((fn) => { captureFlip(); setRank(fn); }, [captureFlip, setRank]);
  const pickProgram = useCallback((key) => { const p = normalizeProgram(key); captureFlip(); writeProgram(p); setProgramState(p); }, [captureFlip]);
  const pickView = useCallback((v) => { writeView(v); setViewState(v); }, []);

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    return (await instance.acquireTokenSilent({ ...graphRequest, account })).accessToken;
  }, [instance, accounts]);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      // Development only: ?homesFixture=50 shows N fake houses (devFixture.js); a production build drops this branch.
      if (process.env.NODE_ENV === 'development') {
        const n = Number(new URLSearchParams(window.location.search).get('homesFixture'));
        if (n > 0) {
          const { fixtureHomes } = await import('./devFixture');
          setRows(fixtureHomes(Math.min(n, 200)));
          setError(null);
          return;
        }
      }
      const token = await getToken();
      const [list, pics] = await Promise.all([loadHomes(token), loadHomePhotos(token)]);
      setRows(list);
      setPhotos(pics);
      setError(null);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoaded(true);
      setRefreshing(false);
    }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  // Held writes must land if the page goes away inside the undo window.
  useEffect(() => {
    const flush = () => pendingRef.current.flushAll({ keepalive: true });
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
    return () => { window.removeEventListener('pagehide', flush); window.removeEventListener('beforeunload', flush); flush(); };
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast((cur) => (cur?.key === toast.key ? null : cur)), UNDO_MS);
    return () => clearTimeout(t);
  }, [toast]);

  // The board's clock: fresh on every load, then once a minute (OPEN NOW, the green dot, Open soon).
  const [today, setToday] = useState(() => new Date());
  useEffect(() => { setToday(new Date()); }, [rows]);
  useEffect(() => {
    const t = setInterval(() => setToday(new Date()), TICK_MS);
    return () => clearInterval(t);
  }, []);
  const watchRows = useMemo(() => watchSet(rows), [rows]);
  const comps = useMemo(() => rows.filter((r) => !isWatch(r) && !isArchived(r)), [rows]);
  const sum = useMemo(() => summary(rows, { today }), [rows, today]);
  const head = useMemo(() => headerSummary(rows, { mode, sort: rank.sort, dropsOnly, today, program }), [rows, mode, rank.sort, dropsOnly, today, program]);
  const board = useMemo(() => boardView(rows, { mode, sort: rank.sort, dropsOnly, today, program }), [rows, mode, rank.sort, dropsOnly, today, program]);
  const scoreById = useMemo(() => new Map(rankHomes(rows, { mode, today, program }).map((v) => [v.row.id, v.score.score])), [rows, mode, today, program]);
  const inactive = useMemo(() => inactiveWatch(rows), [rows]);

  useEffect(() => { onCount?.(watchRows.length); }, [watchRows.length, onCount]);
  useEffect(() => { if (dropsOnly && sum.drops === 0) setDropsOnly(false); }, [dropsOnly, sum.drops]);

  // ── chips ──
  const pickFit = () => setRankFlip((r) => ({ ...r, mode: normalizeMode(null) }));
  const toggleFactor = (key) => setRankFlip((r) => {
    const cur = r.mode.kind === 'custom' ? r.mode.factors : [];
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    return { ...r, mode: normalizeMode({ kind: 'custom', factors: next }) };
  });
  const toggleSort = (key) => setRankFlip((r) => ({ ...r, sort: r.sort === key ? null : key }));

  // ── archive (drop / clear inactive), held for the undo window ──
  const archive = useCallback((targets, label) => {
    if (!targets.length) return;
    const ids = targets.map((r) => r.id);
    const prev = new Map(targets.map((r) => [r.id, r.WatchStatus]));
    const setStatus = (map) => setRows((rs) => rs.map((r) => {
      if (!map.has(r.id)) return r;
      const next = { ...r };
      if (map.get(r.id) === undefined) delete next.WatchStatus; else next.WatchStatus = map.get(r.id);
      return next;
    }));
    const restore = () => setStatus(prev);
    setStatus(new Map(ids.map((id) => [id, 'Archived'])));
    const key = ids.length === 1 ? ids[0] : `batch:${Date.now()}`;
    pendingRef.current.schedule(key, {
      ids,
      restore,
      run: async ({ keepalive } = {}) => {
        try {
          const token = await getToken();
          for (const id of ids) await patchHome(token, id, { WatchStatus: 'Archived' }, { keepalive });
        } catch (e) {
          restore();
          setError(`Could not archive: ${e?.message || e}`);
        }
      },
    });
    setToast({ key, text: label });
  }, [getToken]);

  const undo = useCallback(() => {
    if (toast) pendingRef.current.undo(toast.key);
    setToast(null);
  }, [toast]);

  const dropOne = useCallback((r) => archive([r], `Dropped ${r.Title || r.HomeId}`), [archive]);
  const clearInactive = useCallback(() => archive(inactive, `Cleared ${inactive.length} inactive`), [archive, inactive]);

  const saveNotes = useCallback(async (r, text) => {
    const value = text.trim() ? text : null;
    const before = r.Notes;
    setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, Notes: value ?? undefined } : x)));
    try {
      await patchHome(await getToken(), r.id, { Notes: value });
    } catch (e) {
      setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, Notes: before } : x)));
      setError(`Could not save notes: ${e?.message || e}`);
    }
  }, [getToken]);

  const addPhoto = useCallback(async (r, file) => {
    setUploading(r.id);
    try {
      const token = await getToken();
      const name = await uploadHomePhoto(token, r.HomeId, file, [...photos.keys()]);
      await patchHome(token, r.id, { PhotoRef: name });
      setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, PhotoRef: name } : x)));
      setPhotos(await loadHomePhotos(token));
      setError(null);
      return true;
    } catch (e) {
      setError(`Photo upload failed: ${e?.message || e}`);
      return false;
    } finally {
      setUploading(null);
    }
  }, [getToken, photos]);

  const toggle = (id) => setExpanded((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // ── rows: one open at a time, kept in the URL hash ──
  const toggleRow = useCallback((homeId) => {
    const next = openId === homeId ? null : homeId;
    writeHashId(next);
    setOpenId(next);
  }, [openId]);

  /** Scroll so the open card's top sits just under the sticky chips, if it is off screen. */
  const revealOpen = useCallback((homeId, { smooth = true } = {}) => {
    const item = [...itemRefs.current.entries()].find(([id]) => board.vms.find((v) => v.row.id === id)?.row.HomeId === homeId)?.[1];
    if (!item || typeof item.getBoundingClientRect !== 'function') return;
    const under = stickyRef.current ? stickyRef.current.getBoundingClientRect().bottom : 0;
    const top = item.getBoundingClientRect().top;
    if (top >= under && top < window.innerHeight - 120) return;
    window.scrollTo({ top: window.scrollY + top - under - 8, behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
  }, [board]);

  // Opening a row: once its panel has started to grow, bring it into view.
  useEffect(() => {
    if (!openId || view !== 'rows') return undefined;
    const t = setTimeout(() => revealOpen(openId), 30);
    return () => clearTimeout(t);
  }, [openId]); // eslint-disable-line react-hooks/exhaustive-deps

  // A #HM-… hash on arrival: open the Homes section and scroll to that house once the rows exist.
  const hashHandled = useRef(false);
  useEffect(() => {
    if (!loaded || hashHandled.current || !openId) return;
    hashHandled.current = true;
    if (!board.vms.some((v) => v.row.HomeId === openId)) { writeHashId(null); setOpenId(null); return; }
    openSection('homes', { scroll: false });
    requestAnimationFrame(() => revealOpen(openId, { smooth: false }));
  }, [loaded, board, openId, revealOpen]);

  // FLIP play: items that moved since the snapshot start where they were and slide home.
  useLayoutEffect(() => {
    const from = flipFrom.current;
    flipFrom.current = null;
    if (!from) return;
    itemRefs.current.forEach((el, id) => {
      if (!el || !from.has(id)) return;
      const dy = from.get(id) - (el.getBoundingClientRect().top + window.scrollY);
      if (Math.abs(dy) < 1) return;
      el.style.transition = 'none';
      el.style.transform = `translateY(${dy}px)`;
      requestAnimationFrame(() => {
        el.style.transition = `transform ${FLIP_MS}ms ease`;
        el.style.transform = '';
      });
    });
  });
  const itemRef = (id) => (el) => { if (el) itemRefs.current.set(id, el); else itemRefs.current.delete(id); };

  if (!loaded) return null;
  if (watchRows.length === 0) {
    return error ? <p className="Homes-error">Homes: {error}</p> : null;
  }

  const headerLine = (
    <span data-testid="homes-summary">
      {head.watching} watching
      {' · '}
      <span className={head.drops > 0 ? 'Homes-head-drops' : undefined}>{head.drops} price drop{head.drops === 1 ? '' : 's'}</span>
      {head.top && <>{' · top: '}{head.top.title} ({head.top.score ?? '—'})</>}
    </span>
  );
  const sorted = !!rank.sort || dropsOnly;

  return (
    <CollapsibleSection
      id="homes"
      title="Homes"
      className="Homes"
      summary={headerLine}
      actions={<button type="button" className="Homes-btn" onClick={load} disabled={refreshing}>{refreshing ? 'Refreshing…' : 'Refresh'}</button>}
    >
      <p className="Homes-hint">Prices update when you send Claude your HAR favorites link.</p>
      {error && <p className="Homes-error">{error}</p>}

      <div className="Homes-summary">
        <div className="Homes-tile"><div className="Homes-tile-label">Watching</div><div className="Homes-tile-value">{sum.watching}</div></div>
        <div className="Homes-tile"><div className="Homes-tile-label">Active</div><div className="Homes-tile-value">{sum.active}</div></div>
        <div className="Homes-tile" data-testid="days-tile">
          <div className="Homes-tile-label">Avg days listed</div>
          <div className="Homes-tile-value">{sum.avgDays ?? '—'}</div>
          {sum.medianDays !== null && (
            <div className="Homes-tile-sub">median {sum.medianDays}{sum.lostDays !== null && ` · lost ${sum.lostDays}`}</div>
          )}
        </div>
        <button type="button"
          className={`Homes-tile Homes-tile--drops${sum.drops > 0 ? ' Homes-tile--hot' : ''}${dropsOnly ? ' Homes-tile--on' : ''}`}
          onClick={() => sum.drops > 0 && setDropsOnly((v) => !v)} disabled={sum.drops === 0} aria-pressed={dropsOnly}
          data-testid="drops-tile">
          <div className="Homes-tile-label">Price drops{sum.drops > 0 ? ' ▸' : ''}</div><div className="Homes-tile-value">{sum.drops}</div>
        </button>
        <div className="Homes-tile"><div className="Homes-tile-label">Also for sale</div><div className="Homes-tile-value">{sum.forSale}</div></div>
      </div>

      <div className="Homes-sticky" ref={stickyRef} data-testid="homes-sticky">
      <div className="Homes-chips Homes-chips--program" role="group" aria-label="Swim program">
        <span className="Homes-chips-label">Swim program</span>
        {PROGRAMS.map((p) => (
          <button key={p.key} type="button" className={`Homes-chip${program === p.key ? ' Homes-chip--on' : ''}`} aria-pressed={program === p.key}
            data-program={p.key} onClick={() => pickProgram(p.key)}>{p.label}</button>
        ))}
      </div>
      <div className="Homes-chips" role="group" aria-label="Score">
        <span className="Homes-chips-label">Score</span>
        <button type="button" className={`Homes-chip${mode.kind === 'fit' ? ' Homes-chip--on' : ''}`} aria-pressed={mode.kind === 'fit'} onClick={pickFit}>Fit</button>
        {FACTOR_KEYS.map((k) => {
          const on = mode.kind === 'custom' && mode.factors.includes(k);
          return (
            <button key={k} type="button" className={`Homes-chip${on ? ' Homes-chip--on' : ''}`} aria-pressed={on} onClick={() => toggleFactor(k)}>
              {FACTORS.find((f) => f.key === k).label}
            </button>
          );
        })}
      </div>
      <div className="Homes-chips" role="group" aria-label="Sort">
        <span className="Homes-chips-label">Sort</span>
        {dropsOnly ? (
          <span className="Homes-filter-on">Price drops, biggest % first · <button type="button" className="Homes-link" onClick={() => setDropsOnly(false)}>show all</button></span>
        ) : SORTS.map((s) => (
          <button key={s.key} type="button" className={`Homes-chip Homes-chip--sort${rank.sort === s.key ? ' Homes-chip--on' : ''}`} aria-pressed={rank.sort === s.key} onClick={() => toggleSort(s.key)}>{s.label}</button>
        ))}
        <span className="Homes-viewtoggle" role="group" aria-label="View">
          {[['rows', 'Rows'], ['cards', 'Cards']].map(([k, label]) => (
            <button key={k} type="button" className={`Homes-viewbtn${view === k ? ' Homes-viewbtn--on' : ''}`} aria-pressed={view === k} onClick={() => pickView(k)}>{label}</button>
          ))}
        </span>
      </div>
      </div>
      {inactive.length > 0 && (
        <div className="Homes-toolbar">
          <button type="button" className="Homes-btn Homes-btn--clear" onClick={clearInactive}>Clear inactive ({inactive.length})</button>
        </div>
      )}

      <div className={view === 'rows' ? 'Homes-rows' : 'Homes-cards'} data-testid={`homes-${view}`}>
        {board.vms.map((vm) => {
          const photo = vm.row.PhotoRef ? photos.get(vm.row.PhotoRef) : null;
          const card = (
            <HomeCard vm={vm} mode={mode} program={program} sorted={sorted} photo={photo}
              expanded={expanded.has(vm.row.id)} onToggle={() => toggle(vm.row.id)}
              onDrop={dropOne} onSaveNotes={saveNotes} onPhoto={addPhoto} uploading={uploading === vm.row.id}
              zoneOpen={photoZone === vm.row.id} onZone={(open) => setPhotoZone(open ? vm.row.id : null)} today={today} />
          );
          const panelId = `homes-panel-${vm.row.HomeId}`;
          return (
            <Fragment key={vm.row.id}>
              <div className="Homes-item" ref={itemRef(vm.row.id)}>
                {view === 'rows' ? (
                  <>
                    <HomeRow vm={vm} program={program} photo={photo} now={today} open={openId === vm.row.HomeId} onToggle={() => toggleRow(vm.row.HomeId)} panelId={panelId} />
                    <Accordion id={panelId} open={openId === vm.row.HomeId}>{card}</Accordion>
                  </>
                ) : card}
              </div>
              {board.dividerAfter === vm.row.id && <div className="Homes-tie-divider" data-testid="tie-divider"><span>tied with #1 ↑</span></div>}
            </Fragment>
          );
        })}
      </div>

      <HomesScatter watch={watchRows} comps={comps} fitById={scoreById} />

      {toast && <UndoToast key={toast.key} text={toast.text} onUndo={undo} ttl={UNDO_MS} />}
    </CollapsibleSection>
  );
}
